/* eslint-disable max-lines */
/* eslint-disable react/no-unused-state */
/* eslint-disable @typescript-eslint/naming-convention */

// @TODO make sure we handle loading/error cases properly
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import qs from 'query-string'
import type Bluebird from 'bluebird'
import styled from '@emotion/styled'
import { FormattedMessage } from 'react-intl'
import moment from 'moment'
import { LoadingDots } from '@retailer-platform/shared-components'
import AppHeader from 'legacy/components/AppHeader'
import ContentContainer from 'legacy/components/ContentContainer'
import DocumentTitle from 'legacy/components/DocumentTitle'
import EmptyView from 'legacy/components/EmptyView'
import { addHours, timeZoneOffset } from 'legacy/common/dateUtils'
import {
  AllocationType,
  type CreateShopperShiftResponse,
  type DeleteShopperShiftRequestData,
  type DeliveryHours,
  type Shift,
  type ShiftsApiRequestQuery,
  type ShiftsApiResponseData,
  type ShiftsApiResponseStaffingLevels,
  type ShiftsRouteQuery,
  ShiftType,
  type ShopperWithShifts,
  type UpdateShopperShiftRequestData,
} from 'domains/shifts/components/types'
import {
  type NotificationsContextValue,
  withNotificationsContext,
} from 'legacy/contexts/notifications/NotificationsContext'
import { type Shopper } from 'legacy/routes/shoppers/types'
import ShiftsHeader from 'domains/shifts/components/ShiftsHeader'
import { RecommendedShiftsRow } from 'domains/shifts/components/components/recommended-shifts/RecommendedShiftsRow'
import ShopperShifts from 'domains/shifts/components/ShopperShifts'
import ShiftFilters from 'domains/shifts/components/ShiftFilters'
import ShiftWarning from 'domains/shifts/components/ShiftWarning'
import withApi, { type WithApiInjectedProps } from 'legacy/common/withApi'
import { type ApiDataState, type ApiResponse, DateString } from 'legacy/common/types'
import { errors } from 'utils/error-handling/errors'
import recurringShiftDestroy from 'domains/shifts/components/mutations/recurringShiftDestroy'
import recurringShiftUpdate from 'domains/shifts/components/mutations/recurringShiftUpdate'
import StandardError from 'legacy/common/errors/StandardError'
import { withTrackEventOnMount } from 'utils/events/hocs'
import { calculateRegularShiftHours } from 'domains/shifts/components/utils/calculateRegularShiftHours'
import {
  fetchEarlyHoursForWarehouse,
  type WarehouseEarlyPickingHour,
} from 'domains/shifts/components/utils/fetchEarlyHoursForWarehouse'
import { calculateRecurringShiftUpdateValues } from 'domains/shifts/components/utils/calculateRecurringShiftUpdateValues'
import { trackEvent } from 'utils/events/trackEvent'
import { WeeklyStaffing } from 'domains/shifts/components/components/weekly-staffing/WeeklyStaffing'
import { StaffingColorLegend } from 'domains/shifts/components/StaffingColorLegend'
import { StaffingLevelsRow } from 'domains/shifts/components/components/staffing-levels/StaffingLevelsRow'
import { computeStaffingLevels } from 'domains/shifts/components/components/staffing-levels/utils/computeStaffingLevels'
import { PerformanceOverview } from 'domains/shifts/components/components/performance-overview/PerformanceOverview'
import { type WeeklyStaffingDataCache } from 'domains/shifts/components/components/weekly-staffing/utils/types'
import { type DashRouteComponentProps } from 'legacy/dash-components/dash-route/utils/dashRoute.types'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { PartnerPickShiftsModal } from 'domains/shifts/components/recurring-shifts/PartnerPickShiftsModal'
import { useRecommendedShiftsContext } from 'domains/shifts/components/contexts/RecommendationShiftsContext'
import { useIsDevicePrioritizationEnabled } from 'domains/shifts/utils/hooks/useIsDevicePrioritizationEnabled'
import { useIsRunnerDevicePrioritizationEnabled } from 'domains/shifts/utils/hooks/useIsRunnerDevicePrioritizationEnabled'
import { VerticalType } from 'domains/shifts/api'
import { getPartnerPickRecurringShiftsFormDataForNewShiftCreate } from './recurring-shifts/partnerPickRecurringShiftForm.utils'
import { calculateActualStoreHours } from './utils/shiftsUtils'
import { ShiftManagementContext } from './contexts/ShiftManagementContext'
import {
  type LocationDeliveryHours,
  locationDeliveryHoursQuery,
} from './queries/locationsDeliveryHoursQuery'
import { getPickupHours } from './queries/locationsPickupHoursQuery'

const ShiftsRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
})

const Content = styled.div({
  width: '100%',
})

const StickyContainer = styled.div({
  position: 'sticky',
  top: 0,
  zIndex: 10,
})
interface Props extends WithApiInjectedProps, DashRouteComponentProps, NotificationsContextValue {
  query: ShiftsRouteQuery
  warehouseId: string
  partnerId: string
  flipper: ShiftsFlipper
  userAccountId: string
}

interface DataState {
  shoppers: ShopperWithShifts[]
  staffingLevels: ShiftsApiResponseStaffingLevels[]
  regularHours: DeliveryHours
  actualHours: LocationDeliveryHours
  warehouseEarlyHours?: WarehouseEarlyPickingHour
}

interface ShiftsFlipper {
  minimumStaffedHoursEnabled: boolean
  ultrafastCrossDayDeliveryEnabled?: boolean //https://retailers.instacart.com/flipper/features/ultrafast_cross_day_delivery_enabled
  WarehouseLocationPickupHourEnabled: boolean
}

type State = ApiDataState<DataState> & {
  isWeeklyView: boolean
  weeklyStaffingData: WeeklyStaffingDataCache
  isWeekLoading: boolean
}

const Shifts: FunctionComponent<Props> = props => {
  const [state, setState] = useState<State>({
    loaded: false,
    loading: false,
    error: false,
    shoppers: undefined,
    staffingLevels: undefined,
    regularHours: undefined,
    actualHours: undefined,
    warehouseEarlyHours: undefined,
    isWeeklyView: props.query.week !== undefined,
    weeklyStaffingData: {},
    isWeekLoading: false,
  })

  const { shiftsState, openShiftsModal, setShiftsState } = useShiftsStateContext()
  const { refetchRecommendations } = useRecommendedShiftsContext()

  const recurShiftsToBeNonRecur: number[] = []

  const getRequestParams = () => ({
    partner_id: props.partnerId,
    warehouse_id: props.warehouseId,
    warehouse_location_id: props.query.warehouse_location_id,
    date: props.query.date,
    week: props.query.week,
  })
  const { partner_id, warehouse_id, warehouse_location_id, date, week } = getRequestParams()

  const useDevicePrioritization = useIsDevicePrioritizationEnabled({
    shiftType: shiftsState.shiftType,
    warehouseLocationId: warehouse_location_id,
  })

  const isRunnerDevicePrioritizationEnabled = useIsRunnerDevicePrioritizationEnabled({
    warehouseLocationId: warehouse_location_id,
  })

  const fetchPromiseRef = useRef<Bluebird<ApiResponse<ShiftsApiResponseData, {}>> | undefined>(null)
  const fetchShopperShifts = useCallback(async () => {
    if (!date || !warehouse_location_id) return

    if (fetchPromiseRef.current && fetchPromiseRef.current.isPending()) {
      fetchPromiseRef.current.cancel()
    }

    fetchPromiseRef.current = props.api.get<
      ApiResponse<ShiftsApiResponseData, {}>,
      ShiftsApiRequestQuery
    >(`/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts`, {
      query: {
        date,
        warehouse_location_id,
        device_prioritization_enabled: useDevicePrioritization,
      },
    })
    const { data } = await fetchPromiseRef.current

    return data
  }, [date, partner_id, props.api, useDevicePrioritization, warehouse_id, warehouse_location_id])

  const fetchLocationDeliveryHours = useCallback(async () => {
    if (!date || !warehouse_location_id) return

    // When WarehouseLocationPickupHourEnabled is enabled, we use WHL pickup hours which calls GetStoreHoursByDate RPC
    // If no response or error, we fallback to locationDeliveryHoursQuery
    if (props.flipper.WarehouseLocationPickupHourEnabled) {
      const momentDate = moment(date)
      const googleDate = {
        year: momentDate.year(),
        month: momentDate.month() + 1,
        day: momentDate.date(),
      }

      const response = await getPickupHours({
        retailerLocationIds: [warehouse_location_id],
        startDate: googleDate,
        endDate: googleDate,
        verticalType: VerticalType.PickupHours,
      })

      if (response) {
        const {
          startsAt: { hours: startsAtHour },
          endsAt: { hours: endsAtHour },
        } = response[0]

        return {
          date: date,
          startsAtHour: startsAtHour,
          endsAtHour: endsAtHour,
        }
      }
    }

    const response = await locationDeliveryHoursQuery({
      id: warehouse_location_id,
      dates: [date],
    })
    const {
      warehouseLocation: { deliveryHours },
    } = response

    return deliveryHours[0]
  }, [date, warehouse_location_id, props.flipper.WarehouseLocationPickupHourEnabled])

  const fetchShopperShiftsAndDeliveryHours = useCallback(async () => {
    setState(prevState => ({ ...prevState, loading: true, loaded: false, error: false }))

    if (!date || !warehouse_location_id) return

    try {
      const [resShopperShifts, resLocationDeliveryHours, resWarehouseEarlyHours] =
        await Promise.all([
          fetchShopperShifts(),
          fetchLocationDeliveryHours(),
          fetchEarlyHoursForWarehouse({ warehouseId: warehouse_id }),
        ])
      if (!resShopperShifts && !resLocationDeliveryHours) {
        throw new StandardError('Error fetching shopper shifts, staffing levels, and hours', {
          warehouse_location_id,
          date,
          resShopperShifts,
          resLocationDeliveryHours,
        })
      }

      if (resShopperShifts) {
        const {
          shoppers,
          staffing_levels: staffingLevels,
          delivery_hours: deliveryHours,
        } = resShopperShifts
        const regularHours = calculateRegularShiftHours({
          deliveryHours,
          warehouseEarlyHours: resWarehouseEarlyHours,
        })

        setState(
          prevState =>
            ({
              ...prevState,
              shoppers,
              staffingLevels,
              regularHours,
              warehouseEarlyHours: resWarehouseEarlyHours,
            } as State)
        )
      } else {
        throw new StandardError(
          'Error fetching shopper shifts, staffing levels, and regular store hours',
          {
            warehouse_location_id,
            date,
            resShopperShifts,
          }
        )
      }

      if (resLocationDeliveryHours) {
        setState(prevState => ({ ...prevState, actualHours: resLocationDeliveryHours } as State))
      } else {
        // TODO: Handle this error with a throwable
        errors.captureMessage('Error fetching store delivery hours', {
          level: 'error',
          extra: {
            warehouse_location_id,
            date,
            resLocationDeliveryHours,
          },
        })
      }

      setState(prevState => ({ ...prevState, loading: false, loaded: true } as State))
    } catch (e) {
      errors.captureException(e)

      setState(
        prevState =>
          ({
            ...prevState,
            loading: false,
            loaded: false,
            error: true,
            shoppers: undefined,
            staffingLevels: undefined,
            regularHours: undefined,
            actualHours: undefined,
          } as State)
      )
    }
  }, [date, fetchLocationDeliveryHours, fetchShopperShifts, warehouse_id, warehouse_location_id])

  useEffect(() => {
    fetchShopperShiftsAndDeliveryHours()
  }, [fetchShopperShiftsAndDeliveryHours, props.query])

  const useMinimumStaffedHours = useMemo(
    () => props.flipper.minimumStaffedHoursEnabled && shiftsState.shiftType === ShiftType.Picking,
    [props.flipper.minimumStaffedHoursEnabled, shiftsState.shiftType]
  )

  const handleDeleteShopper = (shopperId: number) => {
    if (!state.shoppers) {
      return
    }

    const shopper = state.shoppers.find(({ id }) => id === shopperId)

    if (!shopper) {
      return
    }

    const shiftIds: number[] = shopper.shifts
      .map(({ id }) => id)
      .filter((n): n is number => n !== undefined)

    setState(
      prevState =>
        ({
          ...prevState,
          shoppers: state.shoppers ? state.shoppers.filter(n => n.id !== shopperId) : [],
        } as State)
    )

    shiftIds.forEach(shiftId => {
      requestDeleteShift(shopperId, shiftId, false)
    })
  }

  const requestDeleteShift = async (shopperId: number, shiftId: number, deleteAll: boolean) => {
    if (!warehouse_location_id) {
      return
    }

    try {
      await updateShiftState(shiftId, s => ({ ...s, busy: true }))

      if (deleteAll) {
        const shift = findShiftInState(shiftId)
        if (!shift) {
          throw new Error('No shift')
        }

        if (shift.recurring_shift_id) {
          trackEvent({
            id: 'shifts.shift_management.recurring_shift_deleted',
            description: 'Delete recurring shift and all related shifts',
            data: {
              shiftId,
              retailerLocationId: warehouse_location_id,
            },
          })

          await recurringShiftDestroy({
            recurringShiftId: String(shift.recurring_shift_id),
            deleteShiftsFromDate: toLocalDateString(new Date(shift.starts_at)),
          })
        } else {
          const err = new Error('The deleteAll flag is only relevant to recurring shifts.')
          errors.captureException(err)
          throw err
        }
      } else {
        trackEvent({
          id: 'shifts.shift_management.shift_deleted',
          description: 'Shift deleted on shift management page',
          data: {
            shiftId,
            retailerLocationId: warehouse_location_id,
          },
        })

        await props.api.delete<{}, DeleteShopperShiftRequestData>(
          `/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts/${shiftId}`,
          {
            data: {
              driver_id: shopperId,
              warehouse_location_id: parseInt(warehouse_location_id, 10),
            },
          }
        )
      }

      updateShopperState(shopperId, shopper => ({
        ...shopper,
        shifts: shopper.shifts.filter(n => n.id !== shiftId),
      }))
    } catch (e) {
      props.notifyError(<FormattedMessage id="shiftsDomain.destroyFailed" />)
    } finally {
      await updateShiftState(shiftId, s => ({ ...s, busy: false }))
    }
  }

  const handleAddShopper = (shopper: Shopper) => {
    trackEvent({
      id: 'shifts.shift_management.shopper_added',
      description: 'Added shopper on shift management page',
      data: {
        shopperId: shopper.id,
        retailerLocationId: warehouse_location_id,
      },
    })

    setState(
      prevState =>
        ({
          ...prevState,
          shoppers: [{ ...shopper, shifts: [] }, ...(state.shoppers || [])],
        } as State)
    )
  }

  const deleteNonRecurringShift = async (shopperId: number, shiftId: number) => {
    if (!shopperId || !warehouse_location_id) {
      return
    }
    await updateShiftState(shiftId, s => ({ ...s, busy: true }))
    trackEvent({
      id: 'shifts.shift_management.enhanced_recurring_shifts.single_shift_deleted',
      description: 'Shift deleted on shift management page using shifts modal',
      data: {
        shiftId,
        retailerLocationId: warehouse_location_id,
        shiftType: shiftsState.shiftType,
      },
    })
    try {
      await props.api.delete<{}, DeleteShopperShiftRequestData>(
        `/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts/${shiftId}`,
        {
          data: {
            driver_id: shopperId,
            warehouse_location_id: parseInt(warehouse_location_id, 10),
          },
        }
      )
      await onShiftDelete(shopperId, shiftId)
      closeShiftsModal()
    } catch (e) {
      // Show error message in recurring shifts modal
    }
  }

  const handleAddShiftUsingRecurringShiftsModal = (
    shopperId: number,
    start: Date,
    end: Date,
    modalStyles?: Object
  ) => {
    const { regularHours } = state
    const zoneOffset = timeZoneOffset(regularHours.start)
    const formData = getPartnerPickRecurringShiftsFormDataForNewShiftCreate(zoneOffset, start, end)
    const newShift = {
      id: undefined,
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
      shift_type: shiftsState.shiftType,
      busy: true,
      days_of_week: [],
      placeholder: true,
    }

    updateShopperState(shopperId, shopper => {
      const shifts = [...shopper.shifts, newShift].sort(
        (a, b) => +new Date(a.starts_at) - +new Date(b.starts_at)
      )

      return { ...shopper, shifts }
    })

    openShiftsModal({
      formData,
      shopperId,
      modalStyles,
      isRecommendationMode: false,
      recommendationType: null,
    })
  }

  const handleAddNonRecurringShift = async (
    shopperId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => {
    const newShift: Shift = {
      id: undefined,
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
      shift_type: shiftsState.shiftType,
      busy: true,
      days_of_week: [],
      on_call: onCall,
    }
    const {
      data: {
        shopper_shift: { id },
      },
    } = await props.api.post<ApiResponse<CreateShopperShiftResponse, {}>>(
      `/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts`,
      {
        data: {
          warehouse_location_id,
          date,
          driver_id: shopperId,
          ...newShift,
        },
      }
    )

    trackEvent({
      id: 'shifts.shift_management.enhanced_recurring_shifts.single_shift_added',
      description: 'Shift added on shift management page using shifts modal',
      data: {
        shopperId,
        shiftId: id,
        retailerLocationId: warehouse_location_id,
        shiftType: shiftsState.shiftType,
      },
    })

    updateShopperState(shopperId, shopper => {
      const sortedShifts = [...shopper.shifts, newShift].sort(
        (a, b) => +new Date(a.starts_at) - +new Date(b.starts_at)
      )
      const shifts = sortedShifts.map(shift =>
        // We block updates to newly created shifts, so we can
        // identify the correct, newly-created shift by reference,
        // even if we have multiple requests in-flight for the same shopper.
        shift === newShift ? { ...shift, id, busy: false } : shift
      )
      return { ...shopper, shifts }
    })
    if (refetchRecommendations) {
      refetchRecommendations()
    }
    closeShiftsModal()
  }

  const getLocalShopperDetails = (shopperId: number) => {
    const { shoppers } = state
    return shoppers.filter(shopper => shopper.id === shopperId)[0]
  }

  const onShiftDelete = (shopperId: number, shiftId: number) => {
    if (!shopperId || !shiftId) {
      return
    }
    updateShopperState(shopperId, shopper => {
      const shifts = shopper.shifts.filter(
        // currently RecurringShiftUpdateMutation does not return `driverShiftId` as part of its response
        // as a result, when we merge two recurring shift into one shift, the frontend is not aware of the new `driverShiftId`
        // and this caused the `shift.id` for the newly merged shift to be null.
        // However, the new recurring shift id is returned by the update request
        // so we can use `shift.recurring_shift_id` to identify the merged shift and delete it from the UI
        shift => ![shift.id, shift.recurring_shift_id].includes(shiftId)
      )
      return { ...shopper, shifts }
    })
  }

  const onShiftSave = async (
    savedShift,
    shopperId: number,
    shiftId: number,
    conflictIds: string[]
  ) => {
    const { regularHours } = state
    const zoneOffset = regularHours ? timeZoneOffset(regularHours.start) : 0

    const newStart = moment
      .utc(props.query.date)
      .startOf('day')
      .add(zoneOffset * -1, 'h')
      .add(savedShift.shiftStartSecondsSinceMidnight, 's')

    const newEnd = newStart.clone().add(savedShift.shiftLengthSeconds, 's')

    const newShift = {
      id: savedShift.driverShiftId ? Number(savedShift.driverShiftId) : null,
      recurring_shift_id: savedShift.id ? Number(savedShift.id) : null,
      starts_at: newStart.toISOString(),
      ends_at: newEnd.toISOString(),
      shift_type: shiftsState.shiftType,
      busy: false,
      days_of_week: savedShift.daysOfWeek,
      on_call: savedShift.onCall,
      recur_end_date: savedShift.recurEndDate ? moment(savedShift.recurEndDate).toString() : null,
    }

    updateShopperState(shopperId, shopper => {
      const shifts = [
        ...shopper.shifts.filter(
          shift =>
            // we need to remove existing shifts to avoid duplications in the UI
            shift.recurring_shift_id != newShift.recurring_shift_id &&
            // we need to remove the previous non recurring shift when it got converted into a recurring one
            shift.id !== shiftId &&
            !conflictIds.includes(shift.recurring_shift_id?.toString() || shift.id?.toString())
        ),
        newShift,
      ].sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at))

      return { ...shopper, shifts }
    })
  }

  const handleAddShift = async (
    shopperId: number,
    start: Date,
    end: Date,
    onCall: boolean,
    modalStyles?: Object
  ) => {
    handleAddShiftUsingRecurringShiftsModal(shopperId, start, end, modalStyles)
  }

  // eslint-disable-next-line max-params
  // remove this when enhanced recurring shifts is enabled for everyone and we don't need to support legacy scheduling experience.
  const handleChangeShift = async (
    shopperId: number,
    shiftId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => {
    const { regularHours } = state

    const shift = findShiftInState(shiftId)

    if (!shift) {
      return
    }

    const updateAll = false
    await updateShiftState(shiftId, s => ({ ...s, busy: true }))

    const shiftUpdates = {
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
      recurring_shift_id: updateAll ? shift.recurring_shift_id : undefined,
    }

    try {
      if (updateAll) {
        if (!regularHours) {
          props.notifyError(<FormattedMessage id="shiftsDomain.updateFailed" />)

          const error = new Error('Attempted Recurring Shift update with no store timezone data')
          errors.captureException(error)

          return
        }

        const { shiftStartSecondsSinceMidnight, shiftLengthSeconds } =
          calculateRecurringShiftUpdateValues(regularHours, start, end)

        const zoneOffset = timeZoneOffset(regularHours.start)

        trackEvent({
          id: 'shifts.shift_management.recurring_shift_edited',
          description: 'Recurring Shift edited',
          data: {
            retailerLocationId: warehouse_location_id,
            shiftId,
          },
        })

        await recurringShiftUpdate({
          recurringShiftId: String(shift.recurring_shift_id),
          localStartsAtHour: (start.getUTCHours() + zoneOffset + 24) % 24,
          localEndsAtHour: (end.getUTCHours() + zoneOffset + 24) % 24,
          shiftStartSecondsSinceMidnight,
          shiftLengthSeconds,
        })
      } else {
        if (!date || !warehouse_location_id) {
          return
        }

        trackEvent({
          id: 'shifts.shift_management.shift_edited',
          description: 'Shift edited on shift management page',
          data: {
            shiftId,
            retailerLocationId: warehouse_location_id,
          },
        })

        await props.api.put<{}, UpdateShopperShiftRequestData>(
          `/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts/${shiftId}`,
          {
            data: {
              ...shift,
              ...shiftUpdates,
              warehouse_location_id: parseInt(warehouse_location_id, 10),
              date,
              driver_id: shopperId,
              on_call: onCall,
            },
          }
        )
      }
      await updateShiftState(shiftId, s => ({ ...s, ...shiftUpdates }))
    } catch (err) {
      props.notifyError(<FormattedMessage id="shiftsDomain.updateFailed" />)
    } finally {
      await updateShiftState(shiftId, s => ({ ...s, busy: false }))
    }
  }

  const updateNonRecurringShift = async (
    shopperId: number,
    shiftId: number,
    recurringShiftId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => {
    const isConvertingRecurringToNonRecurring = !!recurringShiftId
    if (isConvertingRecurringToNonRecurring) {
      recurShiftsToBeNonRecur.push(recurringShiftId)
    }

    if (!date || !warehouse_location_id) {
      return
    }

    trackEvent({
      id: 'shifts.shift_management.enhanced_recurring_shifts.single_shift_edited',
      description: 'Shift edited on shift management page using new shifts modal',
      data: {
        shiftId,
        retailerLocationId: warehouse_location_id,
        shiftType: shiftsState.shiftType,
      },
    })

    const shiftUpdates = {
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
      recurring_shift_id: null,
      on_call: onCall,
      recur_start_date: null,
      days_of_week: [],
    }

    const shift = findShiftInState(shiftId)

    await props.api.put<{}, UpdateShopperShiftRequestData>(
      `/v1/partners/${partner_id}/warehouses/${warehouse_id}/shopper_shifts/${shiftId}`,
      {
        data: {
          ...shift,
          ...shiftUpdates,
          warehouse_location_id: parseInt(warehouse_location_id, 10),
          date,
          driver_id: shopperId,
        },
      }
    )
    await updateShiftState(shiftId, s => ({ ...s, ...shiftUpdates }))
    closeShiftsModal()
  }

  const updateShopperState = (
    shopperId: number,
    getShopperState: (shopper: ShopperWithShifts) => ShopperWithShifts,
    cb?: () => void
  ) => {
    setState(prevState => {
      const shoppers = [...(state.shoppers || [])]
      const shopperIndex = shoppers.findIndex(n => n.id === shopperId)
      const shopper = shoppers[shopperIndex]
      shoppers[shopperIndex] = getShopperState(shopper)

      return { ...prevState, shoppers, weeklyStaffingData: {} } as State
    })
    if (cb) {
      cb()
    }
  }

  const findShiftInState = (shiftId: number): Shift | undefined => {
    const { shoppers } = state

    return (shoppers || []).reduce<Shift | undefined>(
      (s, { shifts }) => s || shifts.find(({ id }) => id === shiftId),
      undefined
    )
  }

  const updateShiftState = (
    shiftId: number,
    getShiftState: (shift: Shift) => Shift
  ): Promise<Shift | undefined> => {
    const { shoppers } = state
    const shift = findShiftInState(shiftId)
    return new Promise(resolve => {
      if (shoppers && shift) {
        const updatedShift = getShiftState(shift)
        setState(
          prevState =>
            ({
              ...prevState,
              shoppers: shoppers.map(shopper => ({
                ...shopper,
                shifts: shopper.shifts.map(s => (s.id === shiftId ? updatedShift : s)),
              })),
              weeklyStaffingData: {},
            } as State)
        )
        resolve(updatedShift)
      } else {
        resolve(undefined)
      }
    })
  }

  const toLocalDateString = (date: Date): DateString => {
    const { regularHours } = state

    const zoneOffset = regularHours ? timeZoneOffset(regularHours.start) : 0

    return new DateString(addHours(date, zoneOffset))
  }

  const handleDailyWeeklyToggle = isWeeklyView => {
    const { query, history } = props
    const mondayOfWeek = moment(query.date).startOf('isoWeek').format('YYYY-MM-DD')

    const newQuery = { ...query }
    if (isWeeklyView) {
      newQuery.week = mondayOfWeek
    } else {
      delete newQuery.week
      newQuery.date = mondayOfWeek === query.week ? query.date : query.week
    }
    history.push(qs.stringifyUrl({ url: window.location.pathname, query: newQuery }))

    setState(prevState => ({ ...prevState, isWeeklyView }))
  }
  const handleNavToDay = date => {
    const { query, history } = props
    const newQuery = { ...query, date }
    delete newQuery.week
    history.push(qs.stringifyUrl({ url: window.location.pathname, query: newQuery }))

    setState(prevState => ({ ...prevState, isWeeklyView: false }))
  }

  const { warehouseId } = props

  if (state.error) {
    return (
      <EmptyView
        style={{ padding: 20 }}
        iconName="warning"
        title={<FormattedMessage id="shiftsDomain.emptyView.title" />}
        body={<FormattedMessage id="shiftsDomain.emptyView.message" />}
      />
    )
  }

  // We should properly display the various states....
  if (!state.loaded || !warehouse_location_id) {
    return <LoadingDots centered />
  }

  const {
    regularHours,
    staffingLevels,
    shoppers,
    actualHours,
    warehouseEarlyHours,
    weeklyStaffingData,
  } = state

  const existingIds = shoppers.map(n => n.id)

  // XXX: Assuming these times don't span timezones across delivery hours,
  // since timezones usually change after midnight I think this is a safe assumption
  const zoneOffset = timeZoneOffset(regularHours.start)

  const { actualStoreOpen, actualStoreClose, regularStoreOpen, regularStoreClose } =
    calculateActualStoreHours(warehouseEarlyHours, regularHours, actualHours)

  const computedStaffingLevels = computeStaffingLevels({
    staffingLevels,
    shoppers,
    storeHours: {
      actualStoreOpen,
      actualStoreClose,
    },
    useDevicePrioritization,
    isRunnerDevicePrioritizationEnabled,
  })

  const closeShiftsModal = () => {
    setState(prevState => {
      // remove the placeholder shift upon modal close
      const shoppers = [...(prevState.shoppers || [])]
      shoppers.forEach((shopper, idx) => {
        shoppers[idx].shifts = shopper.shifts.filter(shift => !shift.placeholder)
      })
      return { ...prevState, shoppers } as State
    })

    setShiftsState(prevState => ({
      ...prevState,
      isShiftsModalOpen: false,
      shiftsFormData: null,
      shiftIdBeingEdited: null,
      recurringShiftIdBeingEdited: null,
      shopperIdBeingEdited: null,
      modalStyles: null,
      selectedRecommendationShiftRange: null,
      recommendationType: null,
    }))

    if (shiftsState.onShiftsModalClose) {
      shiftsState.onShiftsModalClose()
    }
  }

  const renderShifts = () => {
    const { shiftType } = shiftsState

    return (
      <ShiftManagementContext.Provider
        value={{
          storeHours: {
            regularStoreOpen,
            regularStoreClose,
            actualStoreOpen,
            actualStoreClose,
          },
        }}
      >
        <Content data-testid="shopper-shifts-content">
          {state.isWeeklyView ? (
            // WeeklyStaffing is rendered here in renderShifts()
            // because of <ShiftsHeader />'s reliance on context regularStoreOpen and regularStoreClose
            <WeeklyStaffing
              weeklyStaffingData={weeklyStaffingData}
              warehouseEarlyHours={warehouseEarlyHours}
              handleNavToDay={handleNavToDay}
              zoneOffset={zoneOffset}
              zone={regularHours.zone}
              signalWeekIsLoading={isWeekLoading =>
                setState(prevState => ({ ...prevState, isWeekLoading }))
              }
              setWeeklyStaffingData={(weeklyStaffingData: WeeklyStaffingDataCache) => {
                setState(prevState => ({ ...prevState, weeklyStaffingData }))
              }}
              useDevicePrioritization={useDevicePrioritization}
              isRunnerDevicePrioritizationEnabled={isRunnerDevicePrioritizationEnabled}
              {...props}
            />
          ) : (
            <React.Fragment>
              {/*shiftId and recurringShiftId is passed when opened in edit mode: drag or clicking on shit cell*/}
              <PartnerPickShiftsModal
                formData={shiftsState.shiftsFormData}
                date={date}
                warehouseLocationId={warehouse_location_id}
                onCancel={closeShiftsModal}
                onSave={onShiftSave}
                onDelete={onShiftDelete}
                onAddNonRecurringShift={handleAddNonRecurringShift}
                onUpdateNonRecurringShift={updateNonRecurringShift}
                getLocalShopperDetails={getLocalShopperDetails}
                deleteNonRecurringShift={deleteNonRecurringShift}
                modalStyles={shiftsState.modalStyles}
                zoneOffset={zoneOffset}
                devicePrioritizationEnabled={useDevicePrioritization}
                recurShiftsToBeNonRecur={recurShiftsToBeNonRecur}
                ultrafastCrossDayDeliveryEnabled={props.flipper.ultrafastCrossDayDeliveryEnabled}
              />
              <StickyContainer>
                <ShiftsHeader zoneOffset={zoneOffset} zone={regularHours.zone} />

                {useMinimumStaffedHours || useDevicePrioritization ? (
                  <React.Fragment>
                    <StaffingLevelsRow
                      labelId="shiftsDomain.allocation.dedicated.label"
                      tooltipLabelId="shiftsDomain.allocation.dedicated.tooltip"
                      staffingLevels={computedStaffingLevels}
                      allocationType={AllocationType.Dedicated}
                    />

                    <StaffingLevelsRow
                      labelId="shiftsDomain.allocation.oncall.label"
                      tooltipLabelId="shiftsDomain.allocation.oncall.tooltip"
                      staffingLevels={computedStaffingLevels}
                      allocationType={AllocationType.OnCall}
                    />
                  </React.Fragment>
                ) : (
                  <StaffingLevelsRow
                    labelId="shiftsDomain.allocation"
                    staffingLevels={computedStaffingLevels}
                  />
                )}
              </StickyContainer>

              <RecommendedShiftsRow
                warehouseId={warehouseId}
                warehouseLocationId={warehouse_location_id}
                shoppers={shoppers}
                formData={shiftsState.shiftsFormData}
                zoneOffset={zoneOffset}
                userAccountId={props.userAccountId}
              />

              <ShopperShifts
                recurShiftsToBeNonRecur={recurShiftsToBeNonRecur}
                warehouseLocationId={warehouse_location_id}
                zoneOffset={zoneOffset}
                activeShiftType={shiftType}
                shoppers={shoppers}
                onDeleteShopper={handleDeleteShopper}
                onAddShift={handleAddShift}
                onChangeShift={handleChangeShift}
                devicePrioritizationEnabled={useDevicePrioritization}
                ultrafastCrossDayDeliveryEnabled={props.flipper.ultrafastCrossDayDeliveryEnabled}
              />
            </React.Fragment>
          )}

          <StaffingColorLegend />
        </Content>
      </ShiftManagementContext.Provider>
    )
  }

  const { loaded, isWeeklyView, isWeekLoading } = state
  const { shiftType } = shiftsState

  return (
    <ShiftsRoot>
      <DocumentTitle titleId="shiftsDomain.title" />
      <AppHeader title={<FormattedMessage id="shiftsDomain.title" />} />
      <ShiftWarning />
      <ShiftFilters
        isWeeklyView={isWeeklyView}
        handleDailyWeeklyToggle={handleDailyWeeklyToggle}
        onAddShopper={handleAddShopper}
        excludeShopperIds={existingIds}
      >
        <PerformanceOverview
          shiftType={shiftType}
          shoppersForDay={shoppers}
          staffingLevelsForDay={computedStaffingLevels}
          isWeeklyView={isWeeklyView}
          staffingDataForWeek={weeklyStaffingData[week]}
          warehouseEarlyHours={warehouseEarlyHours}
          isLoading={!loaded || isWeekLoading}
          retailerLocationId={warehouse_location_id}
          useDevicePrioritization={useDevicePrioritization}
          isRunnerDevicePrioritizationEnabled={isRunnerDevicePrioritizationEnabled}
          zoneOffset={zoneOffset}
        />
      </ShiftFilters>

      <ContentContainer display="flex" padding="0">
        {renderShifts()}
      </ContentContainer>
    </ShiftsRoot>
  )
}

export default withTrackEventOnMount({
  id: 'shifts.shift_management.viewed',
  description: 'Viewed the shift management page',
})(withNotificationsContext(withApi<Props>(Shifts)))
